import { Box, Card, Checkbox, Grid, Typography, Tooltip, Link } from '@mui/material';
import React, { ReactEventHandler, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FiMail } from 'react-icons/fi';
import { MdOutlinePhone } from 'react-icons/md';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { ICandidate, Interaction, amplitude } from 'wisestep-api';
import {
  WsButton,
  timeSince,
  getDateUsingFormat,
  WsColors,
  WsIconText,
  WsTypography,
  WsTypoColors,
  WsTypoVariants,
  getTurnAroundTimeString,
} from 'wisestep-base';

import ActionWidget from '../../features/candidates/components/actionWidget';
import useCopyToClipboard from '../../features/candidates/talentpool/hooks/useCopyToClipboard';
import {
  emailOptions,
  getAllAlternateNumbers,
  getIdByEmail,
  getSubmissionCardDetails,
  phoneOptions,
  removeDuplicates,
  removeDuplicatesPhNos,
  SmallBoxWithBgColor,
  SmallBoxWithRadius,
} from '../../features/candidates/utils/CandidateHelper';
import SendCandidateForAnalytics from '../../features/conversationWidget/components/SendCandidateForAnalytics';
import conversationWidgetStore from '../../features/conversationWidget/store/conversationWidgetStore';
import StageStatusContextProvider from '../../features/submissions/context/StageStatusContext';
import ErrorLogger from '../../providers/ErrorLogger';
import { getDncMessage } from '../../utils/DncMessage';
import getEncodedQueryParams from '../../utils/getEncodedQueryParams';
import { useConversationWidgetStore } from '../../utils/useConversationWidgetStore';
import { CommunicationLinks } from '../CommunicationLinks';
import PulseIconText from '../PulseIconText';
import UpdateStatusModal from '../UpdateStatusModal';
import { IBreadCrumbsList } from '../WsBreadCrumbs';
import CircularImage from '../WsCustomImage/CircularImage';

export interface IStageStatus {
  applicationStage: string | null;
  applicationState: string | null;
}

export interface WsCandidateCardProps {
  candidate: ICandidate & { isSelected: boolean };
  rightChildren?: React.ReactNode;
  isCheckBox?: boolean;
  checkBoxOnclick?: ReactEventHandler;
  isActiveCard?: boolean;
  gridXs?: number;
  icon?: React.ReactElement;
  onCVClick?: (args: number) => void;
  onActionClick?: (args: number) => void;
  candidateIndex?: number;
  openSingleEmail: (candidate: ICandidate, email: string) => void;
  source?: string;
  interaction?: Interaction;
  stageStatus?: IStageStatus;
  amplitudeCallBack?: (args: string) => void;
  isFrom?: string;
  onTitleClick?: () => void;
  closureId?: string;
  pageRefresh?: () => void;
  array?: string[];
  jobSource?: string;
  backwardpathArray?: IBreadCrumbsList[];
}

interface OpenWidgetState {
  open: boolean;
  action: 'email' | 'sms' | 'call' | 'mapping' | 'submission' | 'task' | 'rtr';
}

enum CandidateType {
  SUBMISSION_LISTING = 'SubmissionListing',
  JOBCRM = 'JobCrm',
}

const WsCandidateCard = ({
  candidate,
  rightChildren,
  isCheckBox,
  checkBoxOnclick,
  isActiveCard = true,
  gridXs,
  onCVClick,
  onActionClick,
  candidateIndex,
  stageStatus,
  amplitudeCallBack,
  isFrom,
  onTitleClick,
  pageRefresh,
  array,
  jobSource,
  backwardpathArray = [],
}: WsCandidateCardProps) => {
  const [handleButtonAct, setHandleButtonAct] = useState<boolean>(candidate?.sendToGRC ?? false);
  const selectedJob = conversationWidgetStore((state) => state.selectedJob);
  const [openStatusModal, setOpenStatusModal] = useState<boolean>(false);
  const [, copy] = useCopyToClipboard();
  const openUpdateStatusModal = () => setOpenStatusModal(true);
  const closeUpdateStatusModal = () => {
    setOpenStatusModal(false);
  };
  const [showMoreContact, setShowMoreContact] = useState(false);
  const [showMoreEmails, setShowMoreEmails] = useState(false);
  const details = getSubmissionCardDetails(candidate);
  const setSelectedEmail = useConversationWidgetStore((state) => state.updateSelectedEmail);
  const setSelectedPhoneNumber = useConversationWidgetStore(
    (state) => state.updateSelectedPhoneNumber
  );
  const [openSubmitCandidate, setOpenSubmitCandidate] = useState(false);
  const candidateStageDate = candidate.stageDate
    ? `with Stage Date ${getDateUsingFormat(candidate.stageDate, 'DD MMMM YYYY, h:mm a')}`
    : '';

  const [openJobSelectionWidget, setOpenJobSelectionWidget] = useState<OpenWidgetState>({
    open: false,
    action: 'email',
  });
  const navigate = useNavigate();

  const openWidget = (field: string, value: string) => {
    switch (field) {
      case 'Send SMS':
        if (isFrom === CandidateType.SUBMISSION_LISTING) {
          amplitudeCallBack && amplitudeCallBack('SMS');
        }
        setSelectedPhoneNumber(value);
        onActionClick && candidateIndex != undefined && onActionClick(candidateIndex);
        // setOpenJobSelectionWidget({ open: true, action: 'sms' });
        navigate(
          `/candidate-layout/profile?id=${
            candidate.candidateId
          }&array=${array}&action=sms&backwardpathArray=${getEncodedQueryParams(
            JSON.stringify(backwardpathArray)
          )}`
        );
        if (amplitudeCallBack) amplitudeCallBack('Send SMS');
        break;
      case 'Call':
        setSelectedPhoneNumber(value);
        onActionClick && candidateIndex != undefined && onActionClick(candidateIndex);
        navigate(
          `/candidate-layout/profile?id=${
            candidate.candidateId
          }&array=${array}&backwardpathArray=${getEncodedQueryParams(
            JSON.stringify(backwardpathArray)
          )}`
        );
        // setOpenJobSelectionWidget({ open: true, action: 'call' });
        //add amplitude here
        break;
      case 'Send Email':
        if (isFrom === CandidateType.SUBMISSION_LISTING) {
          amplitudeCallBack && amplitudeCallBack('Email');
        }
        setSelectedEmail(value);
        onActionClick && candidateIndex != undefined && onActionClick(candidateIndex);
        navigate(
          `/candidate-layout/profile?id=${
            candidate.candidateId
          }&array=${array}&action=email&backwardpathArray=${getEncodedQueryParams(
            JSON.stringify(backwardpathArray)
          )}`
        );
        // setOpenJobSelectionWidget({ open: true, action: 'email' });
        if (amplitudeCallBack) amplitudeCallBack('Send Email');
        break;
      case 'Copy':
        copy(value);
        if (amplitudeCallBack) amplitudeCallBack('Copy');
        break;
    }
  };

  // const updateStageStatus = (selectedStage: string, selectedState: string) => {
  //   const updatedStageStatus: IStageStatus = {
  //     ...candidateStageStatus,
  //     applicationStage: selectedStage,
  //     applicationState: selectedState,
  //   };
  //   if (candidate) {
  //     candidate.applicationStage = selectedStage;
  //     candidate.applicationState = selectedState;
  //   }
  //   setCandidateStageStatus(updatedStageStatus);
  // };

  const updatedInfo = (
    <>
      Updated {timeSince(String(candidate.modifiedOn))} by{' '}
      <Link
        href="#"
        underline="none"
        onClick={() => getIdByEmail(candidate.modifiedBy)}
        sx={{ cursor: 'pointer' }}
      >
        {candidate.modifiedBy}
      </Link>
      {candidateStageDate}
    </>
  );
  const submittedInfo = (
    <>
      Submitted {timeSince(String(candidate.createdOn))} by{' '}
      <Link
        href="#"
        underline="none"
        onClick={() => getIdByEmail(candidate.createdBy)}
        sx={{ cursor: 'pointer' }}
      >
        {candidate.recruiterName}
      </Link>
      {candidate.team?.name || candidate.modifiedByTeamName
        ? `|# ${candidate.modifiedByTeamName ?? candidate.team?.name}`
        : ''}
    </>
  );
  const url = `/candidate-layout/submissions?id=${candidate.candidateId}&interaction=${
    candidate.id
  }&array=${array}&filterParms=${location.search}&backwardpathArray=${getEncodedQueryParams(
    JSON.stringify(backwardpathArray)
  )}`;

  const handleButton = (action: boolean) => setHandleButtonAct(action);
  return (
    <Card>
      <Box pt={1} px={2}>
        <Grid container item xs={12}>
          <Grid item xs={rightChildren ? 8 : 12}>
            <Box>
              <Grid container item xs={12} alignItems="center">
                {isCheckBox ? (
                  <Checkbox
                    sx={{ mb: 0.2, p: 0 }}
                    onClick={checkBoxOnclick}
                    checked={candidate.isSelected ?? false}
                    size="small"
                    style={{ pointerEvents: 'auto' }}
                    disabled={!candidate.email || (candidate.isInDnc ?? false)}
                  />
                ) : (
                  <></>
                )}
                <Box px={0.5}>
                  <CircularImage src={candidate?.profileImage ?? ''} />
                </Box>
                <Box px={0.5}>
                  <RouterLink to={url} style={{ textDecoration: 'none' }}>
                    <WsTypography
                      variant={WsTypoVariants.TITLE}
                      color={isActiveCard ? WsTypoColors.BLACK : WsTypoColors.SECONDARY}
                      cursor="pointer"
                      onClick={() => {
                        amplitude('CandidateView_Redirect_Submission', {
                          clickedOn: 'Redirect Submission',
                          pageName: 'candidate-layout',
                          pageUrl: window.location.href,
                        });
                      }}
                    >
                      {candidate.name}
                    </WsTypography>
                  </RouterLink>
                  <PulseIconText
                    hover={
                      candidate.createdOn
                        ? getDateUsingFormat(candidate.createdOn, 'D MMMM YYYY, HH:mm')
                        : ''
                    }
                    text={submittedInfo}
                  />
                </Box>
              </Grid>
            </Box>
          </Grid>
          {rightChildren && !candidate.isInDnc && (
            <Grid item xs={4} container justifyContent="flex-end">
              {rightChildren}
            </Grid>
          )}
        </Grid>
        {!candidate.isInDnc ? (
          <>
            <Grid direction="row" gap={2} flexWrap="wrap" item container>
              {candidate.email && (
                <CommunicationLinks
                  icon={<FiMail size={14} color={WsColors.TheBlues500} />}
                  emailOrPhNo={candidate.email}
                  objects={emailOptions}
                  onActionClick={openWidget}
                />
              )}
              {removeDuplicates(candidate.alternateEmails, candidate.email).length > 0 && (
                <>
                  {showMoreEmails ? (
                    <>
                      {candidate.alternateEmails.map((email, index) => {
                        if (email != candidate.email)
                          return (
                            <CommunicationLinks
                              key={index}
                              icon={<FiMail size={14} color={WsColors.TheBlues500} />}
                              emailOrPhNo={email}
                              objects={emailOptions}
                              onActionClick={openWidget}
                            />
                          );
                      })}
                    </>
                  ) : (
                    <SmallBoxWithRadius>
                      <Grid
                        item
                        container
                        xs={12}
                        alignItems="center"
                        columnGap={0.5}
                        sx={{ cursor: 'pointer' }}
                      >
                        <WsTypography
                          variant={WsTypoVariants.BODY1}
                          color={WsTypoColors.PRIMARY}
                          onClick={() => setShowMoreEmails(true)}
                          cursor="pointer"
                        >
                          {`+${
                            removeDuplicates(candidate.alternateEmails, candidate.email).length
                          } More`}
                        </WsTypography>
                      </Grid>
                    </SmallBoxWithRadius>
                  )}
                </>
              )}
              {candidate.phoneNumber && (
                <CommunicationLinks
                  icon={<MdOutlinePhone size={16} color={WsColors.TheBlues500} />}
                  emailOrPhNo={candidate.phoneNumber}
                  objects={phoneOptions}
                  onActionClick={openWidget}
                />
              )}
              {removeDuplicatesPhNos(candidate.alternatePhoneNumbers, candidate.phoneNumber ?? '')
                .length > 0 && (
                <>
                  {showMoreContact ? (
                    <>
                      {removeDuplicatesPhNos(
                        candidate.alternatePhoneNumbers,
                        candidate.phoneNumber ?? ''
                      ).map((phone, index) => (
                        <CommunicationLinks
                          key={index}
                          icon={<MdOutlinePhone size={16} color={WsColors.TheBlues500} />}
                          emailOrPhNo={phone}
                          objects={phoneOptions}
                          onActionClick={openWidget}
                        />
                      ))}
                    </>
                  ) : (
                    <SmallBoxWithRadius>
                      <Grid
                        item
                        container
                        xs={12}
                        alignItems="center"
                        columnGap={0.5}
                        sx={{ cursor: 'pointer' }}
                      >
                        <WsTypography
                          variant={WsTypoVariants.BODY1}
                          color={WsTypoColors.PRIMARY}
                          onClick={() => setShowMoreContact(true)}
                          cursor="pointer"
                        >
                          {`+${
                            removeDuplicates(
                              getAllAlternateNumbers(candidate.alternatePhoneNumbers),
                              candidate.phoneNumber ?? ''
                            ).length
                          } More`}
                        </WsTypography>
                      </Grid>
                    </SmallBoxWithRadius>
                  )}
                </>
              )}
            </Grid>
            <Grid container item xs={12} pl={2} py={1} spacing={1}>
              {details?.map((detailsObj, index) => (
                <Grid item xs={gridXs} key={index}>
                  <WsIconText
                    icon={detailsObj.icon ?? <></>}
                    text={detailsObj.labelName ?? ''}
                    opacity={true}
                    textCursor="pointer"
                    onClick={
                      detailsObj.popOverText === 'Wisestep JobCode' ? onTitleClick : undefined
                    }
                    popOverText={`${detailsObj.popOverText}`}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        ) : (
          <Grid container item xs={12} p={4}>
            {candidate.dncDetails && (
              <Tooltip
                title={
                  candidate.dncDetails?.isDeleted
                    ? getDateUsingFormat(
                        String(candidate.dncDetails?.acceptedOn),
                        'D MMMM YYYY, HH:mm'
                      )
                    : ''
                }
              >
                <Typography
                  color={WsColors.Red400}
                  dangerouslySetInnerHTML={{
                    __html: getDncMessage(
                      candidate.dncDetails,
                      candidate.dncDetails?.isAddedByCandidate
                        ? candidate.name
                        : candidate.dncDetails?.modifiedBy ?? ''
                    ),
                  }}
                ></Typography>
              </Tooltip>
            )}
          </Grid>
        )}
      </Box>
      {!candidate.isInDnc && (
        // <CandidateStageStatus interaction={interaction} stageStatus={candidateStageStatus} />
        <Grid item xs={12} container pl={2} columnGap={2} pb={1}>
          {stageStatus?.applicationStage && (
            <SmallBoxWithBgColor>
              <WsTypography color={WsTypoColors.WHITE} bold={true}>
                {stageStatus?.applicationStage}
              </WsTypography>
            </SmallBoxWithBgColor>
          )}
          {stageStatus?.applicationState && (
            <SmallBoxWithBgColor>
              <WsTypography color={WsTypoColors.WHITE} bold={true}>
                {stageStatus?.applicationState}
              </WsTypography>
            </SmallBoxWithBgColor>
          )}
        </Grid>
      )}
      <Grid item xs={12} container sx={{ backgroundColor: WsColors.TheBlues25 }} p={1}>
        <Grid item xs={7} container direction="column">
          {!candidate.isInDnc && (
            <>
              <PulseIconText
                hover={
                  candidate.createdOn
                    ? getDateUsingFormat(candidate.createdOn, 'D MMMM YYYY, HH:mm')
                    : ''
                }
                text={updatedInfo}
              />
              {candidate.turnAroundTime && (
                <PulseIconText
                  text={`Turn Around Time: ${getTurnAroundTimeString(candidate.turnAroundTime)} `}
                />
              )}
            </>
          )}
        </Grid>
        <Grid item xs={5} container justifyContent="flex-end" columnGap={1} alignItems="center">
          {jobSource && jobSource == 'grc' && (
            <WsButton
              className="update-status"
              variant="outlined"
              id="update_status"
              size="medium"
              onClick={() => {
                setOpenSubmitCandidate(true);
              }}
              disabled={handleButtonAct}
            >
              Review and Send
            </WsButton>
          )}
          {!candidate.isInDnc && (
            <WsButton
              className="update-status"
              variant="outlined"
              id="update_status"
              size="medium"
              onClick={() => {
                openUpdateStatusModal();
                if (amplitudeCallBack) amplitudeCallBack('Update Status');
              }}
            >
              Update Status
            </WsButton>
          )}
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            {openStatusModal && (
              <StageStatusContextProvider>
                <UpdateStatusModal
                  candidate={candidate}
                  onCVClick={onCVClick}
                  candidateIndex={candidateIndex}
                  openStatusModal={openStatusModal}
                  onClose={closeUpdateStatusModal}
                  updateStageStatus={pageRefresh && pageRefresh}
                />
              </StageStatusContextProvider>
            )}
          </ErrorBoundary>
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            {openJobSelectionWidget.open && candidate?.id && (
              <ActionWidget
                onSelect={() =>
                  setOpenJobSelectionWidget({ open: false, action: openJobSelectionWidget.action })
                }
                fromPage={'mapping'}
                action={openJobSelectionWidget.action}
                candidateId={candidate?.id}
              />
            )}
          </ErrorBoundary>
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            {openSubmitCandidate && (
              <SendCandidateForAnalytics
                candidate={candidate}
                job={{ ...selectedJob, jobSource: 'grc' }}
                close={() => setOpenSubmitCandidate(false)}
                openSubmitCandidate={openSubmitCandidate}
                sendSuccessfully={handleButton}
              />
            )}
          </ErrorBoundary>
        </Grid>
      </Grid>
    </Card>
  );
};

export default WsCandidateCard;
